'use client';

import classNames from 'classnames';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useCallback, useEffect, useState } from 'react';

const NAV = {
  home: { href: '/', label: 'Home' },
  services: {
    label: 'Services',
    items: [
      { href: '/product-and-engineering', label: 'Product + Engineering' },
      { href: '/brand-and-design', label: 'Brand + Design' },
      { href: '/ip', label: 'Intellectual Property' },
      { href: '/finance', label: 'Finance' },
      { href: '/sourcing-and-recruiting', label: 'Sourcing + Recruiting' },
    ],
  },
  caseStudies: {
    label: 'Case Studies',
    items: [
      { href: '/bentoville', label: 'Bentoville' },
      { href: '/airtime', label: 'Airtime' },
      { href: '/carrot', label: 'Carrot' },
      { href: '/sora', label: 'Sora Union' },
    ],
  },
  letsTalk: { href: 'mailto:hello@all-turtles.com', label: "Let's talk", external: true },
} as const;

function HamburgerIcon({ open }: { open: boolean }) {
  return (
    <span className="relative flex h-2 w-6 flex-none items-center justify-center" aria-hidden>
      <span
        className={classNames(
          'absolute left-0 h-0.5 w-6 bg-current transition-all duration-200',
          open ? 'top-1/2 -translate-y-1/2 rotate-45' : 'top-0',
        )}
      />
      <span
        className={classNames(
          'absolute left-0 h-0.5 w-6 bg-current transition-all duration-200',
          open ? 'top-1/2 -translate-y-1/2 -rotate-45' : 'top-2',
        )}
      />
    </span>
  );
}

function useScrollbarWidth(menuOpen: boolean) {
  const [scrollbarWidth, setScrollbarWidth] = useState(0);

  useEffect(() => {
    const measure = () => {
      setScrollbarWidth(window.innerWidth - document.documentElement.clientWidth);
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);

  // Re-measure when menu closes so we have a correct value (when open, body overflow is hidden so measurement would be 0)
  useEffect(() => {
    if (!menuOpen) {
      const raf = requestAnimationFrame(() => {
        setScrollbarWidth(window.innerWidth - document.documentElement.clientWidth);
      });
      return () => cancelAnimationFrame(raf);
    }
  }, [menuOpen]);

  return scrollbarWidth;
}

export function GlobalNav() {
  const [open, setOpen] = useState(false);
  const pathname = usePathname();
  const scrollbarWidth = useScrollbarWidth(open);

  const close = useCallback(() => setOpen(false), []);

  // Close on route change (e.g. after clicking a link)
  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  // Close on escape
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [close]);

  // Lock body scroll when menu is open (helps on mobile)
  useEffect(() => {
    if (open) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = prev;
      };
    }
  }, [open]);

  const [rendered, setRendered] = useState(false);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (open) {
      setRendered(true);
      // One frame delay so the element mounts first, then transitions in
      const raf = requestAnimationFrame(() => setVisible(true));
      return () => cancelAnimationFrame(raf);
    } else {
      setVisible(false);
      // Unmount after animation completes
      const timer = setTimeout(() => setRendered(false), 350);
      return () => clearTimeout(timer);
    }
  }, [open]);

  // When menu is open, body gets overflow:hidden so the scrollbar disappears and the
  // viewport widens—the same right-5 then shifts the button right. Add the scrollbar
  // width so the hamburger and X stay visually aligned (e.g. "Show scroll bars: always").
  const rightOffset = open ? 20 + scrollbarWidth : 20;

  return (
    <>
      <div
        className="fixed top-[8px] z-[101] flex h-20 w-20 items-center justify-center p-2.5"
        style={{ zIndex: 101, right: `${rightOffset}px` }}
      >
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          className={classNames(
            'flex h-10 w-10 flex-none items-center justify-center transition-colors focus:outline-none',
            open ? 'text-white hover:text-white/80' : 'text-at hover:text-at/80 focus:text-at',
          )}
          aria-expanded={open}
          aria-controls="global-nav-panel"
          aria-label={open ? 'Close menu' : 'Open menu'}
        >
          <HamburgerIcon open={open} />
          <span className="sr-only">{open ? 'Close menu' : 'Menu'}</span>
        </button>
      </div>

      {rendered && (
        <>
          {/* Backdrop */}
          <div
            role="presentation"
            className={classNames(
              'fixed inset-0 bg-navy/80 backdrop-blur-sm transition-opacity duration-300',
              visible ? 'pointer-events-auto z-[99] opacity-100' : 'pointer-events-none z-[99] opacity-0',
            )}
            style={{ zIndex: 99 }}
            aria-hidden
            onClick={close}
          />

          {/* Panel - stop wheel/touch from reaching page scroll (e.g. Lenis) so menu list scrolls instead */}
          <nav
            id="global-nav-panel"
            className={classNames(
              'fixed bottom-0 right-0 top-0 z-[100]',
              'flex',
              'w-full max-w-md flex-col overflow-hidden',
              'bg-navy px-8 pb-12 pt-24 tablet:pt-28',
              'transition-transform duration-300 ease-out',
              visible ? 'translate-x-0' : 'translate-x-full',
            )}
            aria-label="Main navigation"
            onWheel={(e) => e.stopPropagation()}
            onTouchMove={(e) => e.stopPropagation()}
          >
            <div
              className="min-h-0 flex-1 touch-pan-y overflow-y-auto"
              style={{ WebkitOverflowScrolling: 'touch' } as React.CSSProperties}
            >
              <ul className="flex flex-col gap-6 font-soehne text-[clamp(18px,calc(18px+0.5vw),24px)]">
                <li>
                  <Link
                    href={NAV.home.href}
                    onClick={close}
                    className="block text-white transition-colors hover:text-at focus:text-at focus:outline-none"
                  >
                    {NAV.home.label}
                  </Link>
                </li>

                <li>
                  <span className="block text-white/70">{NAV.services.label}</span>
                  <ul className="mt-2 flex flex-col gap-1 pl-0">
                    {NAV.services.items.map((item) => (
                      <li key={item.href}>
                        <Link
                          href={item.href}
                          onClick={close}
                          className="block text-white transition-colors hover:text-at focus:text-at focus:outline-none"
                        >
                          {item.label}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </li>

                <li>
                  <span className="block text-white/70">{NAV.caseStudies.label}</span>
                  <ul className="mt-2 flex flex-col gap-1 pl-0">
                    {NAV.caseStudies.items.map((item) => (
                      <li key={item.href}>
                        <Link
                          href={item.href}
                          onClick={close}
                          className="block text-white transition-colors hover:text-at focus:text-at focus:outline-none"
                        >
                          {item.label}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </li>

                <li className="mt-4 border-t border-white/20 pt-4">
                  <a
                    href={NAV.letsTalk.href}
                    onClick={close}
                    className="block text-white transition-colors hover:text-at focus:text-at focus:outline-none"
                    {...(NAV.letsTalk.external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                  >
                    {NAV.letsTalk.label}
                  </a>
                </li>
              </ul>
            </div>
          </nav>
        </>
      )}
    </>
  );
}
